<template>
  <v-container class="my-5">
    <!-- Header -->
    <v-card-title class="px-0">
      <v-btn icon @click="$router.go(-1)" class="mr-2">
        <v-icon>mdi-arrow-left</v-icon>
      </v-btn>
      <v-icon large :color="areaColor" class="mr-2">mdi-account-check</v-icon>
      <div>
        <div class="headline font-weight-bold">{{ areaName }}</div>
        <div class="caption grey--text">{{ $t('checkin_management') || 'Gestión de Acceso' }}</div>
      </div>
    </v-card-title>

    <!-- Ocupación actual - Card destacado -->
    <v-row class="mt-2">
      <v-col cols="12">
        <v-card class="pa-6" style="border-radius: 16px; border-left: 5px solid;" :style="{ borderLeftColor: areaColor }">
          <div class="d-flex align-center justify-space-between">
            <div>
              <div class="caption grey--text">{{ $t('current_occupancy') || 'Ocupación Actual' }}</div>
              <div class="headline font-weight-bold mt-1" v-if="occupancy">
                {{ occupancy.current_count }} / {{ occupancy.max_capacity }} {{ $t('people') || 'personas' }}
              </div>
            </div>
            <div class="text-right">
              <v-chip :color="statusColor" dark large>
                {{ occupancyPercent }}%
              </v-chip>
            </div>
          </div>
          <v-progress-linear :value="occupancyPercent" :color="statusColor" height="10" rounded class="mt-4"></v-progress-linear>
        </v-card>
      </v-col>
    </v-row>

    <!-- Check-in / Check-out Panel -->
    <v-row class="mt-4">
      <v-col cols="12" lg="8">
        <v-card class="pa-6" style="border-radius: 16px;">
          <div class="overline grey--text mb-5">{{ $t('checkin_by_staff') || 'Registrar Acceso' }}</div>

          <!-- Input de User ID -->
          <div class="d-flex align-center mb-6">
            <v-combobox
              v-model="userId"
              :items="allUsers"
              :loading="loadingUsers || loadingCCSLPUsers"
              :search-input.sync="userSearch"
              item-text="full_name"
              item-value="id"
              :label="$t('user_id') || 'Socio'"
              outlined
              dense
              clearable
              :no-data-text="$t('no_users_found') || 'No se encontraron socios'"
              prepend-inner-icon="mdi-account-search"
              hide-details="auto"
              return-object
              :filter="filterUsers"
            >
              <template v-slot:item="{ item }">
                <v-list-item-content>
                  <v-list-item-title>
                    {{ item.full_name }}
                    <v-chip v-if="item.is_moroso" x-small color="red" class="ml-2" dark>
                      MOROSO
                    </v-chip>
                  </v-list-item-title>
                  <v-list-item-subtitle>{{ item.displaySubtitle }}</v-list-item-subtitle>
                </v-list-item-content>
              </template>
            </v-combobox>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn icon small class="ml-1" v-bind="attrs" v-on="on" @click="sortByAction = !sortByAction"
                  :color="sortByAction ? 'primary' : 'grey'">
                  <v-icon>mdi-sort-numeric-ascending</v-icon>
                </v-btn>
              </template>
              <span>{{ sortByAction ? 'Ordenado por Acción' : 'Ordenado Alfabéticamente' }}</span>
            </v-tooltip>
          </div>

          <!-- Botones Check-in / Checkout -->
          <v-row>
            <v-col cols="12" sm="6">
              <v-btn
                block
                color="success"
                dark
                rounded
                large
                :loading="loadingCheckin"
                :disabled="!userId || loadingCheckin || loadingCheckout"
                @click="doCheckin"
                class="text-capitalize"
              >
                <v-icon left>mdi-login</v-icon>
                {{ $t('check_in') || 'Check-in' }}
              </v-btn>
            </v-col>
            <v-col cols="12" sm="6">
              <v-btn
                block
                color="error"
                dark
                rounded
                large
                :loading="loadingCheckout"
                :disabled="!userId || loadingCheckin || loadingCheckout"
                @click="doCheckout"
                class="text-capitalize"
              >
                <v-icon left>mdi-logout</v-icon>
                {{ $t('check_out') || 'Check-out' }}
              </v-btn>
            </v-col>
          </v-row>

          <!-- Resultado -->
          <v-alert
            v-if="resultMessage"
            :type="resultType"
            dense
            text
            class="mt-5"
            dismissible
            @input="resultMessage = ''"
          >
            {{ resultMessage }}
          </v-alert>
        </v-card>
      </v-col>

      <!-- Panel Info Rápida -->
      <v-col cols="12" lg="4">
        <v-card class="pa-6 h-100" style="border-radius: 16px;">
          <div class="overline grey--text mb-4">{{ $t('area_info') || 'Información del Área' }}</div>
          <div class="body-1 font-weight-bold mb-6">{{ areaName }}</div>

          <v-divider class="mb-4"></v-divider>

          <div class="mb-4">
            <div class="caption grey--text">{{ $t('status') || 'Estado' }}</div>
            <v-chip
              :color="statusColor"
              dark
              small
              class="mt-2"
            >
              <v-icon small left>mdi-circle</v-icon>
              <span v-if="statusColor === 'green'">{{ $t('low') || 'Baja ocupación' }}</span>
              <span v-else-if="statusColor === 'orange'">{{ $t('medium') || 'Media ocupación' }}</span>
              <span v-else-if="statusColor === 'red'">{{ $t('high') || 'Alta ocupación' }}</span>
            </v-chip>
          </div>

          <div class="caption grey--text">{{ $t('area_color') || 'Color' }}</div>
          <div class="d-flex align-center mt-2">
            <div
              style="width: 24px; height: 24px; border-radius: 4px; margin-right: 8px;"
              :style="{ backgroundColor: areaColor }"
            ></div>
            <span class="body-2">{{ areaColor }}</span>
          </div>
        </v-card>
      </v-col>
    </v-row>

    <!-- Historial reciente de check-ins -->
    <v-row class="mt-6">
      <v-col cols="12">
        <v-card class="pa-4" style="border-radius: 16px;">
          <div class="overline grey--text mb-4">{{ $t('recent_checkins') || 'Accesos Recientes' }}</div>

          <!-- Filtros de búsqueda -->
          <v-row class="mb-4" dense align="center">
            <v-col cols="12" sm="6" md="4">
              <v-menu
                v-model="menu"
                :close-on-content-click="false"
                :nudge-right="40"
                transition="scale-transition"
                offset-y
                min-width="290px"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="startDate"
                    label="Inicio"
                    prepend-icon="mdi-calendar"
                    readonly
                    v-bind="attrs"
                    v-on="on"
                    outlined
                    dense
                    small
                  ></v-text-field>
                </template>
                <v-date-picker
                  v-model="startDate"
                  @input="menu = false; onStartDateChange()"
                ></v-date-picker>
              </v-menu>
            </v-col>

            <v-col cols="12" sm="6" md="4">
              <v-menu
                v-model="menu2"
                :close-on-content-click="false"
                :nudge-right="40"
                transition="scale-transition"
                offset-y
                min-width="290px"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="endDate"
                    label="Fin"
                    prepend-icon="mdi-calendar"
                    readonly
                    v-bind="attrs"
                    v-on="on"
                    outlined
                    dense
                    small
                  ></v-text-field>
                </template>
                <v-date-picker
                  v-model="endDate"
                  @input="menu2 = false; onEndDateChange()"
                ></v-date-picker>
              </v-menu>
            </v-col>

            <v-col cols="12" sm="12" md="4" class="d-flex justify-end">
              <v-btn
                icon
                small
                @click="resetFilters"
              >
                <v-icon small>mdi-refresh</v-icon>
              </v-btn>
            </v-col>
          </v-row>

          <v-data-table
            v-if="deduplicatedRecentAccess.length > 0"
            :headers="tableHeaders"
            :items="deduplicatedRecentAccess"
            :loading="loadingHistory"
            :items-per-page="-1"
            dense
            class="elevation-0"
          >
            <template v-slot:item.timestamp="{ item }">
              {{ formatDateTime(item.timestamp) }}
            </template>

            <template v-slot:item.action="{ item }">
              <v-chip
                :color="item.action === 'checkin' ? 'success' : 'error'"
                text-color="white"
                small
                label
              >
                <v-icon small left>{{ item.action === 'checkin' ? 'mdi-login' : 'mdi-logout' }}</v-icon>
                {{ item.action === 'checkin' ? $t('check_in') || 'Check-in' : $t('check_out') || 'Check-out' }}
              </v-chip>
            </template>

            <template v-slot:item.user_name="{ item }">
              <div class="d-flex align-center">
                <v-avatar size="26" class="mr-2 grey lighten-2">
                  <v-icon small>mdi-account</v-icon>
                </v-avatar>
                <div>
                  <div class="body-2 font-weight-bold">{{ getDisplayUserName(item) }}</div>
                </div>
              </div>
            </template>

            <template v-slot:item.notes="{ item }">
              <div class="d-flex align-center flex-wrap">
                <span v-if="item.action != 'auto_checkout'">{{ item.notes || '-' }}</span>
                <v-chip
                  v-if="item.action == 'auto_checkout'"
                  color="info"
                  text-color="white"
                  x-small
                  class="ml-2"
                  label
                >
                  <v-icon x-small left>mdi-clock-fast</v-icon>
                  {{ $t('auto_checkout_applied') || 'Checkout automatico aplicado' }}
                </v-chip>
                <v-chip
                  v-else-if="isOverSixtyMinutes(item)"
                  color="warning"
                  text-color="black"
                  x-small
                  class="ml-2"
                  label
                >
                  {{ $t('over_60_minutes') || 'Mas de 60 min activo' }}
                </v-chip>
              </div>
            </template>

            <template v-slot:no-data>
              <div class="text-center grey--text py-4">
                <v-icon large color="grey lighten-2">mdi-history</v-icon>
                <div class="caption mt-2">{{ $t('no_records') || 'No hay registros disponibles' }}</div>
              </div>
            </template>
          </v-data-table>

          <!-- Paginación Manual - Solo si hay más de 1 página -->
          <div v-if="deduplicatedRecentAccess.length > 0 && totalPages > 1" class="d-flex align-center justify-space-between mt-4">
            <div class="caption grey--text">
              Página {{ currentPage }} de {{ totalPages }} | Total: {{ totalEvents }} eventos
            </div>
            <div class="d-flex gap">
              <v-btn
                icon
                small
                :disabled="currentPage === 1"
                @click="onPageChange(currentPage - 1)"
              >
                <v-icon>mdi-chevron-left</v-icon>
              </v-btn>
              <v-btn
                icon
                small
                :disabled="currentPage === totalPages"
                @click="onPageChange(currentPage + 1)"
              >
                <v-icon>mdi-chevron-right</v-icon>
              </v-btn>
            </div>
          </div>

          <!-- Mensaje cuando no hay registros -->
          <div v-if="deduplicatedRecentAccess.length === 0" class="text-center grey--text py-6">
            <v-icon large color="grey lighten-2">mdi-history</v-icon>
            <div class="caption mt-2">{{ $t('checkin_hint') || 'Los accesos registrados aparecerán aquí.' }}</div>
          </div>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import axios from 'axios'
import store from '@/store'
import { mapGetters, mapState } from 'vuex'

const API_BASE_URL = 'https://clubtest.intrare.app'

export default {
  name: 'area_checkin',
  computed: {
    ...mapGetters(['getProfile']),
    ...mapState(['language']),
    areaId () {
      return this.$route.params.areaId
    },
    occupancyPercent () {
      if (!this.occupancy || !this.occupancy.max_capacity) return 0
      return Math.round(this.occupancy.occupancy_level * 100)
    },
    statusColor () {
      if (!this.occupancy) return 'grey'
      switch (this.occupancy.status) {
        case 'low': return 'green'
        case 'medium': return 'orange'
        case 'high': return 'red'
        default: return 'grey'
      }
    },
    allUsers () {
      const linkedResidentIds = new Set(Object.values(this.userMatchMap).map(u => u.id))

      const systemUsers = this.users
        .filter(u => !linkedResidentIds.has(u.id))
        .map(u => ({
          ...u,
          type: 'user',
          displaySubtitle: u.email || u.address || '',
          is_moroso: false,
          socioNum: Infinity
        }))

      const ccslpUsersList = this.ccslpUsers.map(u => {
        const linkedResident = this.userMatchMap[u.id]
        return {
          ...u,
          type: linkedResident ? 'ccslp_linked' : 'ccslp',
          linkedResidentId: linkedResident ? linkedResident.id : null,
          displaySubtitle: u.socio ? `Socio: ${u.socio}` : '',
          is_moroso: u.is_moroso || false,
          socioNum: u.socio ? parseInt(u.socio, 10) : Infinity
        }
      })

      // Combinar ambas listas
      const combined = [...systemUsers, ...ccslpUsersList]

      const filtered = combined.filter(user => {
        const name = (user.full_name || '').toLowerCase()
        return !name.includes('prueba') && !name.includes('test')
      })

      return filtered.sort((a, b) => {
        if (this.sortByAction) {
          if (a.socioNum !== b.socioNum) {
            return a.socioNum - b.socioNum
          }
        }
        const nameA = (a.full_name || '').toLowerCase()
        const nameB = (b.full_name || '').toLowerCase()
        return nameA.localeCompare(nameB)
      })
    },
    latestActionByUser () {
      const latest = {}

      this.recentAccess.forEach(item => {
        const key = this.getUserHistoryKey(item)
        if (key && !latest[key]) {
          latest[key] = item.action
        }
      })

      return latest
    },
    // Mapeo de CCSLP users vinculados con Django users (por número de acción)
    userMatchMap () {
      // Se comenta por rendimiento, tarda mucho en cargar los registros
      /*
      const residentsByAction = {}
      this.users.forEach(u => {
        const actionNum = this.extractActionNumber(u.username)
        if (actionNum) {
          residentsByAction[actionNum] = u
        }
      })

      const map = {}
      this.ccslpUsers.forEach(ccslp => {
        if (!ccslp.socio) return
        const match = residentsByAction[String(ccslp.socio)]
        if (match) {
          map[ccslp.id] = match
        }
      })
      return map
      */

      // ---> BÚSQUEDA REDUCIDA PARA PRUEBAS <---
      const map = {}
      const testSocios = ['35', '35HG', '301']

      this.ccslpUsers.forEach(ccslp => {
        if (!ccslp.socio) return
        const socioStr = String(ccslp.socio)

        if (testSocios.includes(socioStr)) {
          let match = null
          if (socioStr === '35HG') {
            match = this.users.find(u => u.username === 'dep_35_federico_mendizabal')
          } else {
            match = this.users.find(u => {
              const actionNum = this.extractActionNumber(u.username)
              return actionNum === socioStr
            })
          }
          if (match) map[ccslp.id] = match
        }
      })

      return map
    },
    // Registros duplicados
    deduplicatedRecentAccess () {
      const ccslpUsersByDjangoId = {}

      for (const [ccslpId, djangoUser] of Object.entries(this.userMatchMap)) {
        if (!ccslpUsersByDjangoId[djangoUser.id]) {
          ccslpUsersByDjangoId[djangoUser.id] = []
        }
        ccslpUsersByDjangoId[djangoUser.id].push(ccslpId)
      }

      const filtered = []
      const seenCombinations = new Set()

      for (const item of this.recentAccess) {
        if (item.ccslp_user_id && this.userMatchMap[item.ccslp_user_id]) {
          const djangoUserId = this.userMatchMap[item.ccslp_user_id].id
          const key = `user-${djangoUserId}-${item.action}-${item.timestamp}`

          if (seenCombinations.has(key)) {
            continue
          }
          seenCombinations.add(key)
        }

        filtered.push(item)
      }

      return filtered
    }
  },
  data: () => ({
    areaName: '',
    areaColor: 'primary',
    occupancy: null,
    userId: '',
    loadingCheckin: false,
    loadingCheckout: false,
    resultMessage: '',
    resultType: 'success',
    recentAccess: [],
    loadingHistory: false,
    users: [],
    ccslpUsers: [],
    loadingUsers: false,
    loadingCCSLPUsers: false,
    userSearch: null,
    sortByAction: false,
    nowTick: Date.now(),
    nowTickInterval: null,
    autoCheckoutInterval: null,
    // Paginación
    currentPage: 1,
    itemsPerPage: 50,
    maxItemsPerPage: 200,
    totalEvents: 0,
    totalPages: 0,
    // Filtros de fecha
    startDate: '',
    endDate: '',
    autoCheckoutMinutes: 60,
    historyFilters: {},
    menu: false,
    menu2: false,
    tableHeaders: [
      { text: 'Fecha/Hora', value: 'timestamp', sortable: true },
      { text: 'Usuario', value: 'user_name', sortable: true },
      { text: 'Acción', value: 'action', sortable: false },
      { text: 'Notas', value: 'notes', sortable: false }
    ]
  }),
  created () {
    const today = new Date()
    const year = today.getFullYear()
    const month = String(today.getMonth() + 1).padStart(2, '0')
    const day = String(today.getDate()).padStart(2, '0')
    const localDate = `${year}-${month}-${day}`

    this.startDate = localDate
    this.endDate = localDate

    this.loadAreaInfo()
    this.loadOccupancy()
    this.loadHistory()
    this.getUsers()
    this.getCCSLPUsers()
  },
  mounted () {
    this.nowTickInterval = setInterval(() => {
      this.nowTick = Date.now()
    }, 60000)

    this.autoCheckoutInterval = setInterval(() => {
      this.applyAutoCheckout()
    }, 600000) // 10 minutos en milisegundos

    this.applyAutoCheckout()
  },
  beforeDestroy () {
    if (this.nowTickInterval) {
      clearInterval(this.nowTickInterval)
      this.nowTickInterval = null
    }
    if (this.autoCheckoutInterval) {
      clearInterval(this.autoCheckoutInterval)
      this.autoCheckoutInterval = null
    }
  },
  watch: {
    '$route.params.areaId': {
      handler (newVal, oldVal) {
        if (newVal && newVal !== oldVal) {
          // Limpiar datos previos
          this.areaName = ''
          this.occupancy = null
          this.recentAccess = []
          this.userId = ''
          this.resultMessage = ''

          // Recargar información del área actual
          this.loadAreaInfo()
          this.loadOccupancy()
          this.loadHistory()
        }
      }
    }
  },
  methods: {
    apiCall (method, endpoint, data = null) {
      const config = {
        method,
        url: `${API_BASE_URL}${endpoint}`,
        headers: {
          'Content-Type': 'application/json',
          'Accept-Language': this.language,
          'authorization': 'Token ' + store.getters.getToken
        }
      }
      if (data) config.data = data
      return axios(config)
    },
    filterUsers (item, queryText, itemText) {
      if (!queryText) return true
      const text = queryText.toLowerCase()
      const name = (item.full_name || '').toLowerCase()
      const socio = (item.socio || '').toString().toLowerCase()

      return name.includes(text) || socio.includes(text)
    },
    async getUsers () {
      this.loadingUsers = true
      try {
        const response = await this.apiCall('get', '/residents/all')
        const residents = response.data.residents || []
        this.users = residents.map(resident => ({
          id: resident.idUser.id,
          full_name: `${resident.idUser.first_name} ${resident.idUser.last_name}`,
          email: resident.idUser.email,
          address: resident.address,
          type: 'user',
          ...resident.idUser
        }))
      } catch (error) {
        console.error('Error fetching users:', error)
      } finally {
        this.loadingUsers = false
      }
    },
    async getCCSLPUsers () {
      this.loadingCCSLPUsers = true
      try {
        const response = await this.apiCall('get', '/ccslp/users/?all=true')
        const ccslpUsers = response.data.users || []
        this.ccslpUsers = ccslpUsers.map(user => ({
          id: user.id,
          full_name: user.name,
          socio: user.socio,
          id_dependiente: user.id_dependiente,
          status: user.status,
          is_moroso: user.is_moroso || false,
          activo: user.activo,
          type: 'ccslp'
        }))
      } catch (error) {
        console.error('Error fetching CCSLP users:', error)
      } finally {
        this.loadingCCSLPUsers = false
      }
    },
    async loadAreaInfo () {
      try {
        const response = await this.apiCall('get', '/area/')
        const area = response.data.find(a => a.id === this.areaId)
        if (area) {
          this.areaName = area.name
          this.areaColor = area.color || 'primary'
        }
      } catch (e) {
        console.error('Error loading area info:', e)
      }
    },
    async loadOccupancy () {
      try {
        const res = await this.apiCall('get', `/area/${this.areaId}/occupancy/`)
        this.occupancy = res.data
      } catch (e) {
        console.error('Error loading occupancy:', e)
      }
    },
    async doCheckin () {
      if (!this.userId) return
      this.loadingCheckin = true
      this.resultMessage = ''

      try {
        let endpoint = ''
        if (typeof this.userId === 'object') {
          if (this.userId.type === 'ccslp' || this.userId.type === 'ccslp_linked') {
            endpoint = `/area/${this.areaId}/checkin/ccslp/${this.userId.id}/`
          } else {
            endpoint = `/area/${this.areaId}/checkin/${this.userId.id}/`
          }
        } else {
          endpoint = `/area/${this.areaId}/checkin/${this.userId}/`
        }
        console.log('User ID for check-in:', this.userId)
        console.log('Check-in endpoint:', endpoint)
        await this.apiCall('post', endpoint)
        this.resultType = 'success'
        this.resultMessage = this.$t('checkin_success') || `Entrada registrada correctamente para el usuario.`
        await this.loadOccupancy()
        await this.loadHistory()
        this.userId = ''
        this.$store.commit('snackbar_activate_true')
        this.$store.commit('snackbar_change_color', 'success')
        this.$store.commit('snackbar_change_message', this.resultMessage)
      } catch (error) {
        this.resultType = 'error'
        if (error.response && error.response.status === 400) {
          this.resultMessage = this.$t('checkin_already_active') || 'El usuario ya tiene un check-in activo en esta área.'
        } else if (error.response && error.response.status === 404) {
          this.resultMessage = this.$t('user_not_found') || 'Usuario o área no encontrado.'
        } else if (error.response && error.response.status === 403) {
          this.resultMessage = this.$t('no_permission') || 'Sin permiso para realizar esta acción.'
        } else {
          this.resultMessage = this.$t('error_generic') || 'Ocurrió un error inesperado.'
        }
      } finally {
        this.loadingCheckin = false
      }
    },
    async doCheckout () {
      if (!this.userId) return
      this.loadingCheckout = true
      this.resultMessage = ''

      try {
        let endpoint = ''

        if (typeof this.userId === 'object') {
          if (this.userId.type === 'ccslp' || this.userId.type === 'ccslp_linked') {
            endpoint = `/area/${this.areaId}/checkout/ccslp/${this.userId.id}/`
          } else {
            endpoint = `/area/${this.areaId}/checkout/${this.userId.id}/`
          }
        } else {
          endpoint = `/area/${this.areaId}/checkout/${this.userId}/`
        }

        await this.apiCall('post', endpoint)
        this.resultType = 'success'
        this.resultMessage = this.$t('checkout_success') || 'Salida registrada correctamente.'
        await this.loadOccupancy()
        await this.loadHistory()
        this.userId = ''
        this.$store.commit('snackbar_activate_true')
        this.$store.commit('snackbar_change_color', 'success')
        this.$store.commit('snackbar_change_message', this.resultMessage)
      } catch (error) {
        this.resultType = 'error'
        if (error.response && error.response.status === 404) {
          this.resultMessage = this.$t('no_active_checkin') || 'No hay check-in activo para este usuario en el área.'
        } else if (error.response && error.response.status === 403) {
          this.resultMessage = this.$t('no_permission') || 'Sin permiso para realizar esta acción.'
        } else {
          this.resultMessage = this.$t('error_generic') || 'Ocurrió un error inesperado.'
        }
      } finally {
        this.loadingCheckout = false
      }
    },
    async saveConfig () {
      this.loadingConfig = true
      this.configMessage = ''
      const payload = {}
      if (this.configName) payload.name = this.configName
      if (this.configMaxCapacity) payload.max_capacity = this.configMaxCapacity
      if (this.configColor) payload.color = this.configColor
      try {
        await this.apiCall('patch', `/area/${this.areaId}/config/`, payload)
        this.configType = 'success'
        this.configMessage = this.$t('config_saved') || 'Configuración guardada correctamente.'
        await this.loadAreaInfo()
        await this.loadOccupancy()
      } catch (error) {
        this.configType = 'error'
        if (error.response && error.response.status === 403) {
          this.configMessage = this.$t('no_permission') || 'Sin permiso para configurar esta área.'
        } else if (error.response && error.response.status === 400) {
          this.configMessage = this.$t('no_fields_to_update') || 'No se proporcionó ningún campo para actualizar.'
        } else {
          this.configMessage = this.$t('error_generic') || 'Ocurrió un error inesperado.'
        }
      } finally {
        this.loadingConfig = false
      }
    },
    async loadHistory () {
      this.loadingHistory = true
      try {
        // Construir parámetros de query
        const params = new URLSearchParams()
        params.append('page', this.currentPage)
        params.append('limit', this.itemsPerPage)
        params.append('auto_checkout_minutes', this.autoCheckoutMinutes)

        if (this.startDate) {
          params.append('start_date', this.startDate)
        }
        if (this.endDate) {
          params.append('end_date', this.endDate)
        }

        const res = await this.apiCall('get', `/area/${this.areaId}/history/?${params.toString()}`)
        const historyData = res.data.history || []
        this.recentAccess = historyData.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))

        // Actualizar metadata de paginación
        if (res.data.pagination) {
          this.totalEvents = res.data.pagination.total_events || 0
          this.totalPages = res.data.pagination.total_pages || 0
        }

        // Guardar filtros aplicados
        if (res.data.filters) {
          this.historyFilters = res.data.filters
        }
      } catch (e) {
        console.error('Error loading history:', e)
        this.recentAccess = []
      } finally {
        this.loadingHistory = false
      }
    },
    formatDateTime (dateString) {
      if (!dateString) return '-'
      const date = new Date(dateString)
      return date.toLocaleString(this.language || 'es-ES')
    },
    getUserHistoryKey (item) {
      if (item && item.user_id) return `user-${item.user_id}`
      if (item && item.user_name) return `name-${item.user_name.toLowerCase().trim()}`
      return null
    },
    // Extrae el número de acción de un username con formato ccslp_{accion}_{nombre}
    extractActionNumber (username) {
      if (!username) return null
      const parts = username.split('_')
      return parts.length >= 2 ? parts[1] : null
    },
    // Obtiene el nombre del usuario considerando vinculaciones CCSLP ↔ Django
    getDisplayUserName (item) {
      // Si es usuario del sistema, devolver su nombre
      if (item.user_id && !item.ccslp_user_id) {
        return item.user_name
      }

      // Si es CCSLP, verificar si está vinculado a un Django user
      if (item.ccslp_user_id) {
        const linkedResident = this.userMatchMap[item.ccslp_user_id]
        if (linkedResident) {
          // Mostrar el nombre del residente vinculado
          return linkedResident.full_name || linkedResident.username
        }
        // Si no está vinculado, mostrar nombre del CCSLP user
        return item.user_name || item.ccslp_user_name
      }

      return item.user_name
    },
    // Determina si un registro es de un CCSLP user vinculado a un Django user
    isLinkedCCSLPUser (item) {
      return item.ccslp_user_id && this.userMatchMap[item.ccslp_user_id]
    },
    // Obtiene la clave única de un registro considerando vinculaciones
    getUniqueUserKey (item) {
      if (item.user_id) {
        return `user-${item.user_id}`
      }

      if (item.ccslp_user_id) {
        const linkedResident = this.userMatchMap[item.ccslp_user_id]
        if (linkedResident) {
          // Si está vinculado, usar la clave del residente vinculado
          return `user-${linkedResident.id}`
        }
        // Si no está vinculado, usar la clave del CCSLP
        return `ccslp-${item.ccslp_user_id}`
      }

      return null
    },
    isOverSixtyMinutes (item) {
      if (!item || item.action !== 'checkin' || !item.timestamp) return false

      const key = this.getUserHistoryKey(item)
      if (!key || this.latestActionByUser[key] !== 'checkin') return false

      const elapsedMs = this.nowTick - new Date(item.timestamp).getTime()
      if (elapsedMs < 0 || Number.isNaN(elapsedMs)) return false

      return elapsedMs >= 60 * 60 * 1000
    },
    onStartDateChange () {
      if (this.startDate && this.endDate && this.startDate > this.endDate) {
        this.endDate = this.startDate
      }
      this.currentPage = 1
      this.loadHistory()
    },
    onEndDateChange () {
      if (this.startDate && this.endDate && this.endDate < this.startDate) {
        this.startDate = this.endDate
      }
      this.currentPage = 1
      this.loadHistory()
    },
    onPageChange (page) {
      this.currentPage = page
      this.loadHistory()
    },
    onItemsPerPageChange (limit) {
      if (limit > this.maxItemsPerPage) {
        this.itemsPerPage = this.maxItemsPerPage
      } else {
        this.itemsPerPage = limit
      }
      this.currentPage = 1
      this.loadHistory()
    },
    resetFilters () {
      const today = new Date()
      const year = today.getFullYear()
      const month = String(today.getMonth() + 1).padStart(2, '0')
      const day = String(today.getDate()).padStart(2, '0')
      const localDate = `${year}-${month}-${day}`

      this.startDate = localDate
      this.endDate = localDate
      this.currentPage = 1
      this.itemsPerPage = 50
      this.autoCheckoutMinutes = 60
      this.loadHistory()
    },
    async applyAutoCheckout () {
      try {
        const params = new URLSearchParams()
        params.append('auto_checkout_minutes', this.autoCheckoutMinutes)
        params.append('limit', 200)

        const res = await this.apiCall('get', `/area/${this.areaId}/history/?${params.toString()}`)
        const history = res.data.history || []

        const autoCheckoutEvents = history.filter(event => event.action === 'auto_checkout')

        if (autoCheckoutEvents.length === 0) {
          console.log('No hay auto-checkouts pendientes')
          return
        }

        for (const event of autoCheckoutEvents) {
          try {
            let primaryEndpoint = ''
            let fallbackEndpoint = null

            if (event.ccslp_user_id) {
              // Verificar si es un usuario ccslp_linked (tiene Django user vinculado)
              const linkedUser = this.userMatchMap[event.ccslp_user_id]
              primaryEndpoint = `/area/${this.areaId}/auto-checkout/ccslp/${event.ccslp_user_id}/`
              if (linkedUser) {
                // Fallback al endpoint de Django en caso de que el CCSLP no resuelva el linked
                fallbackEndpoint = `/area/${this.areaId}/auto-checkout/${linkedUser.id}/`
              }
            } else if (event.user_id) {
              primaryEndpoint = `/area/${this.areaId}/auto-checkout/${event.user_id}/`
            } else {
              console.warn('No se puede determinar tipo de usuario para auto-checkout:', event)
              continue
            }

            try {
              await this.apiCall('post', primaryEndpoint, {
                auto_checkout_minutes: this.autoCheckoutMinutes
              })
              console.log(`Auto-checkout persistido para ${event.user_name}`)
            } catch (primaryError) {
              // Si hay endpoint de fallback (ccslp_linked) y el error no es 400 (tiempo), intentar con Django user
              if (fallbackEndpoint && primaryError.response?.status !== 400) {
                await this.apiCall('post', fallbackEndpoint, {
                  auto_checkout_minutes: this.autoCheckoutMinutes
                })
                console.log(`Auto-checkout persistido (via usuario vinculado) para ${event.user_name}`)
              } else {
                throw primaryError
              }
            }
          } catch (error) {
            if (error.response?.status === 400) {
              console.log(`${event.user_name} aún no tiene 60+ minutos`)
            } else {
              console.error(`Error auto-checkout para ${event.user_name}:`, error.response?.data || error)
            }
          }
        }

        // 4. Recargar historial para mostrar cambios
        await this.loadHistory()
        console.log('Historial actualizado')
      } catch (error) {
        console.error('Error en applyAutoCheckout:', error)
      }
    }
  }
}
</script>

<style scoped>
</style>
